/**
 * Origin → Queue adapter.
 *
 * Translates an Origin intake payload (direct from tiles 02/03/04, or mirrored
 * from HubSpot for tile 01 orders) into a queue Request with its Subject(s).
 *
 * Landing state: every incoming line lands at the Intake phase — pm_assessment
 * OPEN, everything else PENDING — and carries its Origin spec. The PMA then runs
 * intake rules in the PMA view to confirm deliverables + attributes. Standard
 * catalog items skip development (everything but pm_assessment is not_required).
 */

import type {
  Request, Master, Single, Subject, DeliverableEntry, Submitter, ActivityEntry,
  EstimationMode,
} from '@/lib/dd-queue/types';
import type {
  DdOriginPayload,
  OriginClonePayload,
  OriginModifyBundlePayload,
  OriginSpec,
  OriginUpdateType,
} from '@/lib/dd-queue/types/originPayload'
import type {
  OriginNewProductSinglePayload,
  OriginNewProductCartPayload,
  OriginModifyPayload,
  OriginNewOrderPayload,
  OriginLineItem,
  OriginUser,
  OriginRef,
  OriginDiscontinueReplacePayload,
  OriginNewProductBundlePayload,
  OriginSopWarning,
} from '@/types/origin'
import type { SOPWarning } from '@/data/mockSignBuilderCatalog'
import {
  isOriginNewProductCartPayload,
  isOriginNewProductSinglePayload,
} from '@/lib/sales-workflow/signBuilderCartPayload';
import {
  normalizeRoutingPreference,
  routingToEstimationMode,
} from '@/lib/sales-workflow/ncoRoutingPreference'
import { STEP_LIBRARY } from '@/lib/dd-queue/data/steps';
import { requiredStepsForChanges } from '@/lib/dd-queue/modify';
import { calcDueDateHours } from '@/lib/dd-queue/dueAt';
import { mintFixtureLineKeys } from '@/lib/dd-queue/lineKey';
import { BUILD_TBD_FLAG } from '@/lib/dd-queue/buildTbdFlag';

/** Preserve SOP levels for SdSopWarnings; unknown levels fall back to info. */
function toSopWarnings(raw: OriginSopWarning[] | undefined | null): SOPWarning[] {
  if (!raw?.length) return []
  return raw.map((w) => ({
    level:
      w.level === 'error' || w.level === 'warning' || w.level === 'info' ? w.level : 'info',
    text: w.text,
  }))
}

/** Flag when rep described intent instead of speccing the build (DD-DESCRIBE-001b). */
function buildTbdFlags(spec: OriginSpec | undefined): string[] {
  return spec?.build_mode === 'describe' ? [BUILD_TBD_FLAG] : []
}
// ─── Helpers ──────────────────────────────────────────────────────────────────

// DEMO-SHIM: display-format request ids minted client-side. Derived from the
// payload's submitted_at so the SAME submission gets the SAME id across PDQ
// reloads — the Origin status/notes bridge correlates on request id, and a
// random id would orphan notes and break status tracking every reload.
// RUKH's backend assigns real request ids at integration.
//
// DD-BE-PR2b-FILES / CodeRabbit S2 reversal: synthetic *line* SKUs are derived
// from origin_ref + line index (explicit catalog/reference SKUs stay as-is) so
// subjectMutations keys stay stable across reload/hydrate.
type IdType = 'ORD' | 'NEW' | 'CHG' | 'BUN'
function genId(seedStr: string | undefined, type: IdType): string {
  if (seedStr) {
    let h = 5381;
    for (let i = 0; i < seedStr.length; i++) h = ((h << 5) + h + seedStr.charCodeAt(i)) >>> 0;
    return `${type}-2026-${String(1000 + (h % 9000))}`;
  }
  return `${type}-2026-1000`;
}

/** Real catalog / reference SKU only — never fabricate NEW|BNDL|LINE-…-L{n} display tokens. */
function explicitSkuOnly(explicit: string | null | undefined): string | null {
  const trimmed = typeof explicit === 'string' ? explicit.trim() : '';
  return trimmed || null;
}

function toSubmitter(u: OriginUser): Submitter {
  return { id: u.id, name: u.name, email: u.email, role: u.role, team: u.team };
}

/** Adapter seed for local/fixture rows — use real submitted_at (never viewer Date.now()). */
function originSubmitActivity(submittedAt: string | undefined, e: string): ActivityEntry {
  return { t: submittedAt?.trim() ? submittedAt : 'just now', e };
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** DD-DUE-24H-002 — 24 weekday hours + round (fixtures / adapter default). */
function dueFromSubmitted(iso: string): { dueDate: string; dueAtIso: string } {
  const due = calcDueDateHours(iso);
  return { dueDate: due.display, dueAtIso: due.iso };
}

const DEV_STEPS = STEP_LIBRARY
  .filter(s => s.key !== 'pm_assessment')
  .map(s => s.key);

/**
 * Standard products need no PD work — PM Assessment is auto-cleared on landing
 * and everything else is not required. They never sit in the PM queue and never
 * block a master; they ride along in the proof packet.
 */
function standardDeliverables(): Record<string, DeliverableEntry> {
  const d: Record<string, DeliverableEntry> = { pm_assessment: { status: 'complete', assignee: null } };
  for (const k of DEV_STEPS) d[k] = { status: 'not_required', assignee: null };
  return d;
}

/** Custom products land at intake: pm_assessment open, everything else pending.
 *  (Install need is an INDICATOR — Subject.installRequired — not a step.)
 *  uinPreAssigned: when the client has already decided the UIN (Origin intake), the
 *  Assign UIN step is pre-marked not_required so PD never has to action it. */
function intakeDeliverables(uinPreAssigned = false): Record<string, DeliverableEntry> {
  const d: Record<string, DeliverableEntry> = { pm_assessment: { status: 'open', assignee: null } };
  for (const k of DEV_STEPS) d[k] = { status: 'pending', assignee: null };
  if (uinPreAssigned) {
    d['uin'] = { status: 'not_required', assignee: null };
  }
  return d;
}

/**
 * Reduced deliverable profile for a bundle line. The bundle has NO design,
 * estimation, or sales review — those happen on its individual custom members,
 * and by the time they're all approved there is nothing left to review on the
 * bundle itself (it only carries the info the rep entered up front). It runs
 * Exactly: Triage → SKU → UIN → Web Image → Product Review → Publish to PIM →
 * Publish to Web. (A bundle is its own product with its own web image.) The
 * actionable steps are gated on members being SKU'd (see resolveSubject's bundle
 * gate), but they land `pending` here like any phase step.
 */
const BUNDLE_REQUIRED_STEPS = new Set(['sku', 'uin', 'web_image', 'product_review', 'pim_publish', 'web_publish']);
function bundleDeliverables(): Record<string, DeliverableEntry> {
  const d: Record<string, DeliverableEntry> = {};
  for (const step of STEP_LIBRARY) {
    if (step.key === 'pm_assessment')          d[step.key] = { status: 'open', assignee: null };
    else if (BUNDLE_REQUIRED_STEPS.has(step.key)) d[step.key] = { status: 'pending', assignee: null };
    else                                        d[step.key] = { status: 'not_required', assignee: null };
  }
  return d;
}

/**
 * Design Jobs (live customer orders, no PIM) skip triage and run exactly:
 * Design → Estimation (parallel by default) → Sales Review → Pre-press.
 * Product Review is not_required on design-jobs (not a PDS gate for ORD).
 * Every other step is not_required and will be hidden from the UI automatically.
 * STD-CUSTOM-QUEUE-001: also used for Standard-Custom catalog lines (order work, not product-dev).
 */
function designJobDeliverables(): Record<string, DeliverableEntry> {
  const d: Record<string, DeliverableEntry> = {};
  for (const s of STEP_LIBRARY) d[s.key] = { status: 'not_required', assignee: null };
  d['design']          = { status: 'pending', assignee: null };
  d['estimation']      = { status: 'pending', assignee: null };
  d['sales_approval']  = { status: 'pending', assignee: null };
  d['production_file'] = { status: 'pending', assignee: null };
  // pm_assessment stays not_required — triage is skipped entirely.
  return d;
}

/** A standard/catalog line with Standard-Custom values — needs designer work (Origin isStandardCustom). */
function isStandardCustom(li: OriginLineItem): boolean {
  return li.item_type !== 'custom' && !!li.custom_field_values?.length
}

function baseSubject(over: Partial<Subject> & Pick<Subject, 'productType'> & { sku?: string | null }): Subject {
  const isStandard = over.productType === 'standard';
  return {
    sku: over.sku ?? null,
    pimMode: 'pim',
    estimationMode: 'standard',
    installRequired: false,
    deliverables: isStandard ? standardDeliverables() : intakeDeliverables(),
    age: 0,
    // Origin lines enter Triage the moment they're ingested.
    triageEnteredAt: Date.now(),
    designTime: 0,
    designSessions: 0,
    qty: 1,
    flags: [],
    ...over,
  };
}

// ─── Tile 04 — New Product (Sign Builder) ────────────────────────────────────

function fromNewProductSingle(p: OriginNewProductSinglePayload): Single {
  const spec = p.spec;
  const reqId = p.ref ?? genId(p.submitted_at, 'NEW');
  const sku = explicitSkuOnly(spec.reference_sku?.sku);
  const sopWarnings = toSopWarnings(p.sop_warnings);
  const knownUin = spec.known_uin || null;
  const flags = buildTbdFlags(spec);

  const subject = baseSubject({
    sku, productType: 'custom',
    qty: spec.quantity || 1,
    flags,
    sopWarnings,
    spec,
    ...(knownUin ? {
      knownUin,
      // Override the default intakeDeliverables so uin is pre-marked not_required.
      deliverables: intakeDeliverables(true),
    } : {}),
  });

  return {
    ...subject,
    id: p.ref ?? genId(p.submitted_at, 'NEW'), isMaster: false, path: 'new', vip: false,
    product: spec.product_description || reqId, sku,
    customer: p.client || p.submitted_by.team,           // client from Request Info; else submitter team
    submitter: toSubmitter(p.submitted_by),
    submitDate: fmtDate(p.submitted_at), ...dueFromSubmitted(p.submitted_at),
    submittedAtIso: p.submitted_at,
    flags,
    sopWarnings,
    activity: [originSubmitActivity(p.submitted_at, `${p.submitted_by.name} submitted new product via Origin`)],
  };
}

/** F-042 (Taylor) — a Sign Builder line is named by its Product Description (what the child row's
 *  Product column prints); nothing when Sales left it blank. */
function productDescriptionName(spec: OriginSpec): { name: string } | Record<string, never> {
  const text = typeof spec.product_description === 'string' ? spec.product_description.trim() : '';
  return text ? { name: text } : {};
}

/** F-042 (Taylor) — the Request Info "Project Description" of a multi-line request (parent row's
 *  Product column); undefined when absent / blank (rows submitted before it travelled). */
function projectDescriptionOf(p: { project_description?: unknown }): string | undefined {
  const text = typeof p.project_description === 'string' ? p.project_description.trim() : '';
  return text || undefined;
}

/** SYS-003: live RUKH wire — tile `new_product` + `products[]` (no top-level spec). */
function fromNewProductCart(p: OriginNewProductCartPayload): Master {
  const submitter = toSubmitter(p.submitted_by);
  const components: Subject[] = p.products.map((prod, index) => {
    const spec = prod.spec;
    const sku = explicitSkuOnly(spec.reference_sku?.sku);
    const sopWarnings = toSopWarnings(prod.sop_warnings);
    const knownUin = spec.known_uin || null;
    return baseSubject({
      sku,
      productType: 'custom',
      ...productDescriptionName(spec),
      qty: spec.quantity ?? 1,
      flags: buildTbdFlags(spec),
      sopWarnings,
      spec,
      ...(knownUin
        ? {
            knownUin,
            deliverables: intakeDeliverables(true),
          }
        : {}),
    });
  });
  const projectDescription = projectDescriptionOf(p);

  return {
    id: p.ref ?? genId(p.submitted_at, 'NEW'),
    isMaster: true,
    path: 'new',
    vip: false,
    product: `Sign Builder Order — ${components.length} products`,
    meta: `${components.length} custom lines`,
    ...(projectDescription ? { projectDescription } : {}),
    customer: p.client || p.submitted_by.team,
    submitter,
    submitDate: fmtDate(p.submitted_at),
    ...dueFromSubmitted(p.submitted_at),
    submittedAtIso: p.submitted_at,
    flags: [],
    children: components,
    activity: [
      originSubmitActivity(
        p.submitted_at,
        `${p.submitted_by.name} submitted ${components.length} new products via Origin (Sign Builder cart)`,
      ),
    ],
  };
}

// ─── Tile 02 — Modify Existing Product ───────────────────────────────────────

// A minimal OriginSpec carrying only the design-brief fields (used so a design
// change's description + reference files surface in PD's Design Brief card).
function designBriefSpec(description: string, attachments: OriginSpec['attachments']): OriginSpec {
  return {
    product_description: description, category: null, base_substrate: null,
    width: null, height: null, single_double: 'single',
    graphic_method: null, vinyl_type: null, rta_color: null, laminate: null,
    taping: null, is_paneled: false, paneling_deferred_to_internal: false,
    panel_count: null, panel_width: null, panel_height: null, panel_notes: null,
    special_order_tier: null, hardware_needed: false, hardware_description: null,
    grommets: null, hem: null, pole_pocket: false, rounded_corners: null,
    mounting_holes: null, white_ink: null, reference_sku: null,
    design_notes: description || null,
    ada_color_spec: null, ada_pictogram: null, ada_tactile_text: null, ada_braille: null, ada_build_notes: null,
    attachments: attachments ?? [], quantity: 1, notes: null,
  };
}

// DD-CHG-CARD-001 — the Modify form sends Sign Builder categories (sb2…sb5) rather than the
// prototype's `spec` / `design`: Graphics & Print (sb3) is a design change, the other three are
// spec changes. Both make the line custom PD work and the design one carries a brief + files.
// F-099 slice 7: Parts & Materials is a spec change, Artwork Update a design change.
const DESIGN_CHANGE_TYPES: ReadonlySet<string> = new Set(['design', 'sb3', 'artwork']);
const SPEC_CHANGE_TYPES: ReadonlySet<string> = new Set(['spec', 'sb2', 'sb4', 'sb5', 'parts']);
export function hasDesignChange(types: readonly string[]): boolean {
  return types.some(t => DESIGN_CHANGE_TYPES.has(t));
}
export function hasSpecChange(types: readonly string[]): boolean {
  return types.some(t => SPEC_CHANGE_TYPES.has(t));
}

type ModifyDesignBrief = { description?: string; design_notes?: string | null; attachments?: OriginSpec['attachments'] };

/** The brief text of a design change: the prototype's `description`, the form's `design_notes`. */
function designBriefText(design: ModifyDesignBrief): string {
  return design.description ?? design.design_notes ?? '';
}

type ModifyArtworkBrief = { aspects?: string[]; notes?: string | null };

/**
 * F-099 slice 7 — the design brief of a change request: Graphics & Print's notes + files, and an
 * Artwork Update's "what changes" line + description (its files ride the same design block).
 */
function modifyDesignBrief(p: Pick<OriginModifyPayload, 'changes'>): { text: string; attachments: OriginSpec['attachments'] } | null {
  const design = p.changes?.design as ModifyDesignBrief | undefined;
  const artwork = p.changes?.artwork as ModifyArtworkBrief | undefined;
  if (!design && !artwork) return null;
  const lines: string[] = [];
  if (artwork) {
    const aspects = (artwork.aspects ?? []).map(a => a.charAt(0).toUpperCase() + a.slice(1)).join(', ');
    const head = aspects ? `Artwork update — ${aspects}` : 'Artwork update';
    lines.push(artwork.notes ? `${head}: ${artwork.notes}` : head);
  }
  const designText = design ? designBriefText(design) : '';
  if (designText) lines.push(designText);
  return { text: lines.join('\n'), attachments: design?.attachments ?? [] };
}

/** Human-readable label for a set of change types, used as the Master CHG title.
 *  F-136 (Justin, 2026-10-12): substrate / dimensions / laminate / finishing / parts changes read
 *  as one "Build" update. */
function changeTypesLabel(types: OriginUpdateType[]): string {
  const LABELS: Record<string, string> = {
    price: 'Price', uin: 'UIN', web: 'Web', spec: 'Build',
    design: 'Design', discontinue: 'Discontinue',
    sb2: 'Build', sb3: 'Design', sb4: 'Build', sb5: 'Build', sb6: 'Notes',
    std_custom: 'Standard-Custom', parts: 'Build', artwork: 'Design',
  };
  const unique = [...new Set(types.map(t => LABELS[t] ?? t))];
  if (unique.length === 1) return `${unique[0]} Update`;
  if (unique.length === 2) return `${unique[0]} & ${unique[1]} Update`;
  return 'Multi-Category Update';
}

function fromModify(p: OriginModifyPayload): Single | Master {
  // Normalize legacy single-change payloads into the multi-change shape.
  const types: OriginUpdateType[] = (p.change_types && p.change_types.length)
    ? p.change_types
    : (p.update_type ? [p.update_type] : []);

  // Normalize product list — multi-product path uses products[], single uses product.
  const rawProducts = p.products ?? (p.product ? [p.product] : []);

  // ── Multi-product: bundle into a Master CHG, one child per SKU ──────────────
  if (rawProducts.length > 1) {
    // F-131 — the master's label covers every product's types (own sets included).
    const allTypes = [...types];
    for (const prod of rawProducts) for (const t of prod.change_types ?? []) if (!allTypes.includes(t)) allTypes.push(t);
    const changeLabel  = changeTypesLabel(allTypes);
    const submitter    = toSubmitter(p.submitted_by);
    const submitDate   = fmtDate(p.submitted_at);
    const pmFlag       = p.pm_flag ? [`⚠ PM Review needed — may need a new product instead of a modification`] : [];
    const sharedBrief = modifyDesignBrief(p);

    const children: Subject[] = rawProducts.map(prod => {
      // F-131 / F-132 — a product with its own change set (the form's "Same changes as the
      // first product" unticked) is read from its entry; the others share the request's.
      const own = Array.isArray(prod.change_types) && prod.changes !== undefined;
      const childTypes: OriginUpdateType[] = own ? (prod.change_types ?? []) : types;
      const childChanges = own ? (prod.changes ?? {}) : (p.changes ?? {});
      const brief = own ? modifyDesignBrief({ changes: prod.changes ?? {} }) : sharedBrief;
      const isCustomWork = hasSpecChange(childTypes) || hasDesignChange(childTypes);
      let spec: OriginSpec | undefined;
      if (hasDesignChange(childTypes) && brief) {
        spec = designBriefSpec(brief.text, brief.attachments);
      }
      return baseSubject({
        sku: prod.sku,
        name: prod.label,
        productType: isCustomWork ? 'custom' : 'standard',
        deliverables: modifyDeliverables(childTypes),
        spec,
        qty: 1,
        flags: pmFlag,
        modify: {
          changeTypes: childTypes,
          changes: childChanges as Record<string, unknown>,
          why: p.why,
          product: prod,
          ...(p.pm_flag ? { pmFlagReasons: p.pm_flag_reasons ?? [] } : {}),
        },
      });
    });

    return {
      id: p.ref ?? genId(p.submitted_at, 'CHG'), isMaster: true, path: 'chg', vip: false,
      product: `${changeLabel} — ${rawProducts.length} Products`,
      meta: p.why,
      customer: p.submitted_by.team,
      submitter,
      submitDate, ...dueFromSubmitted(p.submitted_at),
      submittedAtIso: p.submitted_at,
      flags: pmFlag,
      activity: [originSubmitActivity(
        p.submitted_at,
        `${submitter.name} requested ${changeLabel.toLowerCase()} for ${rawProducts.length} products via Origin${p.pm_flag ? ' [flagged for PM review]' : ''}`,
      )],
      children,
    };
  }

  // ── Single product (original path) ──────────────────────────────────────────
  const product = rawProducts[0] ?? p.product!;
  const required = requiredStepsForChanges(types);

  const deliverables: Record<string, DeliverableEntry> = {
    pm_assessment: { status: 'open', assignee: null },
  };
  for (const k of DEV_STEPS) {
    deliverables[k] = required.has(k)
      ? { status: 'pending', assignee: null }
      : { status: 'not_required', assignee: null };
  }

  // Design/spec changes are real custom PD work; the rest are catalog-data edits.
  const isCustomWork = hasSpecChange(types) || hasDesignChange(types);

  // Carry a design brief so the description + reference files reach PD.
  let spec: OriginSpec | undefined;
  const brief = modifyDesignBrief(p);
  if (hasDesignChange(types) && brief) {
    spec = designBriefSpec(brief.text, brief.attachments);
  }

  const typeList = types.join(', ');
  return {
    id: p.ref ?? genId(p.submitted_at, 'CHG'), isMaster: false, path: 'chg', vip: false,
    triageEnteredAt: Date.now(),
    product: product.label, sku: product.sku,
    productType: isCustomWork ? 'custom' : 'standard',
    customer: p.submitted_by.team,
    submitter: toSubmitter(p.submitted_by),
    submitDate: fmtDate(p.submitted_at), ...dueFromSubmitted(p.submitted_at),
    submittedAtIso: p.submitted_at,
    pimMode: 'pim', estimationMode: 'standard',
    installRequired: false,
    deliverables,
    spec,
    // Carry the full change request so the PMA can show what's changing and
    // pre-fill attributes from the SKU with the modifications indicated.
    modify: {
      changeTypes: types, changes: (p.changes ?? {}) as Record<string, unknown>, why: p.why, product,
      // The specific criteria the request violated (set when forced past the warning).
      ...(p.pm_flag ? { pmFlagReasons: p.pm_flag_reasons ?? [] } : {}),
    },
    age: 0, designTime: 0, designSessions: 0, qty: 1,
    // Change categories + reason live in the PMA Change Request panel, not Flags.
    // The PM flag (option 3 in the new-product warning) surfaces as a flag chip.
    flags: p.pm_flag
      ? [`⚠ PM Review needed — may need a new product instead of a modification`]
      : [],
    activity: [originSubmitActivity(p.submitted_at, `${p.submitted_by.name} requested ${typeList || 'product'} change via Origin${p.pm_flag ? ' [flagged for PM review]' : ''}`)],
  };
}

// ─── Tile — Discontinue + Replace (existing SKU → master of CHG + NEW) ────────

// Deliverables for a given set of change types, landing at Triage (pm_assessment open).
function modifyDeliverables(types: OriginUpdateType[]): Record<string, DeliverableEntry> {
  const required = requiredStepsForChanges(types);
  const d: Record<string, DeliverableEntry> = { pm_assessment: { status: 'open', assignee: null } };
  for (const k of DEV_STEPS) {
    d[k] = required.has(k) ? { status: 'pending', assignee: null } : { status: 'not_required', assignee: null };
  }
  return d;
}

function fromDiscontinueReplace(p: OriginDiscontinueReplacePayload): Master {
  const submitter  = toSubmitter(p.submitted_by);
  const submitDate = fmtDate(p.submitted_at);

  // Child 1 — discontinue the existing catalog SKU (CHG).
  const discChild: Subject = baseSubject({
    sku: p.product.sku,
    productType: 'standard',
    name: p.product.label,
    pimMode: 'pim',
    estimationMode: 'standard',
    deliverables: modifyDeliverables(['discontinue']),
    modify: { changeTypes: ['discontinue'], changes: { discontinue: p.discontinue }, why: p.why, product: p.product },
    qty: 1,
    flags: [],
  });

  // Child 2 — the replacement product (NEW); references the retired SKU's artwork.
  const newChild: Subject = baseSubject({
    sku: `${p.product.sku}-NEW`,
    productType: 'custom',
    name: p.new_spec.product_description || `${p.product.sku} replacement`,
    spec: p.new_spec,
    pimMode: 'pim',
    estimationMode: 'standard',
    deliverables: intakeDeliverables(),
    qty: p.new_spec.quantity || 1,
    flags: [],
  });

  const replaceLabel = `Discontinue + Replace ${p.product.sku}`;
  return {
    id: p.ref ?? genId(p.submitted_at, 'CHG'), isMaster: true, path: 'chg', vip: false,
    product: replaceLabel,
    meta: replaceLabel,
    customer: p.submitted_by.team,
    submitter,
    submitDate, ...dueFromSubmitted(p.submitted_at),
    submittedAtIso: p.submitted_at,
    flags: [],
    children: [discChild, newChild],
    activity: [originSubmitActivity(p.submitted_at, `${submitter.name} requested discontinue + replace for ${p.product.sku} via Origin`)],
  };
}

// ─── Tile 03 — Quote → Catalog (multi-line → master) ─────────────────────────

function fromClone(p: OriginClonePayload): Request {
  const children: Subject[] = p.lines.map(line => baseSubject({
    sku: line.quote_reference_sku ?? `Q-${line.line_no}`,
    productType: 'custom',
    name: line.name,
    qty: line.qty || 1,
    flags: line.line_notes ? [line.line_notes] : [],
  }));

  const submitter = toSubmitter(p.submitted_by);
  const submitDate = fmtDate(p.submitted_at);

  if (children.length === 1) {
    const c = children[0];
    return {
      ...c,
      id: p.ref ?? genId(p.submitted_at, 'NEW'), isMaster: false, path: 'new', vip: false,
      product: p.lines[0].name, sku: c.sku,
      customer: submitter.team, submitter,
      submitDate, ...dueFromSubmitted(p.submitted_at),
    submittedAtIso: p.submitted_at,
      flags: c.flags,
      activity: [originSubmitActivity(p.submitted_at, `${submitter.name} cloned ${p.source.estimate_number} via Origin`)],
    };
  }

  const master: Master = {
    id: p.ref ?? genId(p.submitted_at, 'NEW'), isMaster: true, path: 'new', vip: false,
    product: `Quote ${p.source.estimate_number} → Catalog`,
    meta: `Master · ${children.length} lines · from ${p.source.pdf_name}`,
    customer: submitter.team, submitter,
    submitDate, ...dueFromSubmitted(p.submitted_at),
    submittedAtIso: p.submitted_at,
    flags: [],
    children,
    activity: [originSubmitActivity(p.submitted_at, `${submitter.name} cloned ${children.length} lines from ${p.source.estimate_number}`)],
  };
  return master;
}

// ─── Tile 01 — New Customer Order (master; custom + standard children) ───────

function lineToSubject(
  li: OriginLineItem,
  index: number,
  isDesignJob = false,
  originRef?: string,
  /** NCO-ROUTE-PREF-001 (492799e): order routing seeds every line. */
  estimationMode: EstimationMode = 'standard',
): Subject {
  const isCustom = li.item_type === 'custom'
  // For design jobs: every custom line gets the four-step design workflow (no triage).
  // For product-dev orders: one-off custom lines (pim_mode !== 'pim') ride along like
  // standard lines — their design is handled outside PDQ until a later phase.
  const isOneOff = isCustom && !isDesignJob && li.pim_mode !== 'pim'
  // Explicit SKUs untouched; otherwise deterministic LINE-{origin_ref}-L{n} (PR2b-FILES / Item 0).
  const sku = isCustom
    ? explicitSkuOnly(li.sku)
    : (typeof li.sku === 'string' && li.sku.trim() ? li.sku.trim() : li.name)
  const sopWarnings = toSopWarnings(li.sop_warnings)
  return baseSubject({
    sku,
    productType: isCustom ? 'custom' : 'standard',
    estimationMode,
    ...(isDesignJob && isCustom ? { deliverables: designJobDeliverables() }
      : isOneOff               ? { deliverables: standardDeliverables() }
      // Standard-Custom: catalog product, order work — same four-step track as a
      // design job (no Triage). Applies even on mixed orders whose pim-bound
      // custom siblings go through Triage (Origin lineToSubject).
      : isStandardCustom(li)   ? { deliverables: designJobDeliverables() }
      : {}),
    // Sales-indicated PIM intent: 'pim' (add to catalog) or 'oneoff' (no record).
    // Standard/catalog lines always keep the default 'pim' from baseSubject.
    pimMode: isCustom ? (li.pim_mode ?? 'oneoff') : 'pim',
    // "Install needed?" from the salesperson — an indicator for the estimator, not a step.
    installRequired: li.install_needed ?? false,
    name: li.name,
    // Sign Builder lines carry their full spec — the one PIM-free channel of
    // build/material detail for a custom line (incl. any reference SKU).
    spec: li.spec,
    // STD-CUSTOM-NCO-001 — Standard-Custom per-order values (FR CustomFieldsCard).
    ...(li.custom_field_values?.length ? { customFieldValues: li.custom_field_values } : {}),
    qty: li.quantity || 1,
    flags: buildTbdFlags(li.spec),
    ...(sopWarnings.length ? { sopWarnings } : {}),
  })
}

function fromNewOrder(p: OriginNewOrderPayload): Request | null {
  // Routing: plain standard-only → HubSpot only. Custom and/or Standard-Custom
  // lines enter PDQ (STD-CUSTOM-QUEUE-001 / Origin hasPdqWork).
  //
  //   • Any PIM-bound custom (pim_mode === 'pim') → Product-dev: triage required.
  //     SC siblings on the same order still run the design-job track individually.
  //
  //   • No PIM-bound customs → Design Job (one-off custom and/or SC-only): skip
  //     triage; four steps only. HubSpot estimation ticket at submit (Trigger A).
  const pimBound = (li: OriginLineItem) => li.item_type === 'custom' && li.pim_mode === 'pim';
  const hasPdqWork = (li: OriginLineItem) => li.item_type === 'custom' || isStandardCustom(li);

  if (!p.line_items.some(hasPdqWork)) return null;  // standard-only — HubSpot only

  const isDesignJob = !p.line_items.some(pimBound);

  // NCO-ROUTE-PREF-001 (492799e): typed routing_preference → every line's estimationMode.
  // Seed is resolve-time only — does not write dd_workflow_lines.estimation_mode on ingest.
  const routingPreference = normalizeRoutingPreference(p.routing_preference);
  const estimationMode = routingToEstimationMode(routingPreference);

  const submitter  = toSubmitter(p.submitted_by);
  const submitDate = fmtDate(p.submitted_at);
  const vip        = /rush|expedite/i.test(p.turn_time);
  const children   = p.line_items.map((li, i) =>
    lineToSubject(li, i, isDesignJob, p.ref, estimationMode),
  );

  const activity: ActivityEntry[] = [
    originSubmitActivity(
      p.submitted_at,
      `${submitter.name} submitted ${isDesignJob ? 'design job' : 'order'} via Origin · ${children.length} line item${children.length === 1 ? '' : 's'}`,
    ),
  ];

  const orderMeta = {
    customer: p.customer_name, submitter,
    plantCode: p.plant_code, siteCode: p.site_code ?? undefined,
    siteLocation: p.location || undefined,
    shippingAddress: p.shipping_address || undefined,
    dispatchMethod: p.dispatch_method || undefined,
    submitDate, ...dueFromSubmitted(p.submitted_at),
    submittedAtIso: p.submitted_at,
    routingPreference,
    flags: vip ? ['Rush job'] : [],
  };

  const jobTypeProp = isDesignJob ? { jobType: 'design' as const } : {};

  if (children.length === 1) {
    const c = children[0];
    return {
      ...c,
      id: p.ref ?? genId(p.submitted_at, 'ORD'), isMaster: false, path: 'ord', vip,
      product: p.line_items[0].name, sku: c.sku,
      ...jobTypeProp,
      ...orderMeta,
      // orderMeta flags are order-level (Rush) — merge, don't clobber, line flags (Build TBD).
      flags: [...c.flags, ...orderMeta.flags],
      activity,
    };
  }

  return {
    id: p.ref ?? genId(p.submitted_at, 'ORD'), isMaster: true, path: 'ord', vip,
    product: p.customer_name + ' Order',
    meta: p.project_description || p.notes || `${isDesignJob ? 'Design job' : 'Master order'} · ${children.length} products`,
    ...jobTypeProp,
    ...orderMeta,
    children,
    activity,
  };
}

// ─── Tile: New Product Bundle (multiple Sign Builder products → master) ──────

function fromNewProductBundle(p: OriginNewProductBundlePayload): Request | null {
  const isBundle = !!p.is_bundle;
  const productCount = p.products?.length ?? 0;
  if (productCount === 0 && !isBundle) return null;

  // Single product, NOT a bundle → behave identically to a plain new_product.
  if (!isBundle && productCount === 1) {
    const first = p.products[0];
    const { products: _products, is_bundle: _isBundle, bundle_name: _bundleName, notes: _notes, existing_members: _existing, ...base } = p;
    return fromNewProductSingle({
      ...base,
      tile: 'new_product',
      new_product_variant: 'single',
      spec: first.spec,
      sop_warnings: first.sop_warnings,
    });
  }

  const submitter  = toSubmitter(p.submitted_by);
  const submitDate = fmtDate(p.submitted_at);

  // Component children — the custom new products built in this submission.
  const components: Subject[] = (p.products ?? []).map((prod, index) => {
    const spec     = prod.spec;
    const sku      = explicitSkuOnly(spec.reference_sku?.sku);
    const sopWarnings = toSopWarnings(prod.sop_warnings);
    const knownUin = spec.known_uin || null;
    return baseSubject({
      sku, productType: 'custom',
      // F-042 — only the plain multi-product request; sellable bundle members are untouched.
      ...(isBundle ? {} : productDescriptionName(spec)),
      qty: prod.qty ?? spec.quantity ?? 1, flags: buildTbdFlags(spec), sopWarnings, spec,
      ...(knownUin ? {
        knownUin,
        deliverables: intakeDeliverables(true),
      } : {}),
    });
  });

  if (!isBundle) {
    const master: Master = {
      id: p.ref ?? genId(p.submitted_at, 'NEW'), isMaster: true, path: 'new', vip: false,
      product: `Sign Builder Order — ${components.length} products`,
      meta: `${components.length} custom lines`,
      customer: p.client || p.submitted_by.team,
      submitter,
      submitDate, ...dueFromSubmitted(p.submitted_at),
      submittedAtIso: p.submitted_at,
      flags: [],
      children: components,
      activity: [
        originSubmitActivity(
          p.submitted_at,
          `${p.submitted_by.name} submitted ${components.length} new products via Origin (Sign Builder)`,
        ),
      ],
    };
    return master;
  }

  // ── Product Bundle ──────────────────────────────────────────────────────────
  // Existing catalog SKUs ride along as standard members (already done, no PD
  // work) — same treatment as standard lines on an order.
  const existing: Subject[] = (p.existing_members ?? []).map(m => baseSubject({
    sku: m.sku,
    productType: 'standard',
    name: m.sku,
    deliverables: standardDeliverables(),
    qty: m.qty ?? 1,
    flags: [],
  }));

  const bundleName = p.bundle_name?.trim() || 'Product Bundle';
  // The synthetic bundle line: its own (to-be-assigned) SKU + reduced workflow.
  const bundleChild: Subject = baseSubject({
    sku: null, productType: 'custom', name: bundleName,
    isBundle: true, deliverables: bundleDeliverables(), qty: 1, flags: [],
  });

  const memberCount = components.length + existing.length;
  const master: Master = {
    id: p.ref ?? genId(p.submitted_at, 'BUN'), isMaster: true, path: 'new', vip: false,
    isBundle: true, bundleName,
    product: bundleName,
    meta: `Bundle · ${memberCount} product${memberCount === 1 ? '' : 's'}`,
    customer: p.client || p.submitted_by.team,
    submitter,
    submitDate, ...dueFromSubmitted(p.submitted_at),
    submittedAtIso: p.submitted_at,
    flags: [],
    // Components first, then existing ride-alongs, then the bundle line last.
    children: [...components, ...existing, bundleChild],
    activity: [
      originSubmitActivity(
        p.submitted_at,
        `${p.submitted_by.name} submitted bundle "${bundleName}" — ${components.length} new + ${existing.length} existing product${memberCount === 1 ? '' : 's'} via Origin`,
      ),
    ],
  };
  return master;
}

// ─── Tile: Modify Bundle (add / remove / reprice bundle members → CHG Master) ─

function fromModifyBundle(p: OriginModifyBundlePayload): Master | null {
  const totalChanges = p.added.length + p.removed.length + p.modified.length;
  if (totalChanges === 0) return null;

  const submitter  = toSubmitter(p.submitted_by);
  const submitDate = fmtDate(p.submitted_at);

  // CHG child for the bundle line itself — documents membership delta.
  const bundleChg: Subject = baseSubject({
    sku: p.bundle_sku,
    productType: 'standard',
    name: p.bundle_name,
    isBundle: true,
    qty: 1,
    flags: [],
    deliverables: modifyDeliverables(['bundle_edit']),
    modify: {
      changeTypes: ['bundle_edit'],
      changes: { bundle_edit: { existing: p.existing_members ?? [], added: p.added, removed: p.removed, modified: p.modified } },
      why: p.notes || '',
      product: {
        sku: p.bundle_sku,
        label: p.bundle_name,
        category: 'Bundle',
        current_price: 0,
        current_uin: null,
        status: 'active',
      },
    },
  });

  // Newly added products that need full PD intake (type: 'new').
  const newProductChildren: Subject[] = p.added
    .filter((m): m is Extract<typeof m, { type: 'new' }> => m.type === 'new')
    .map((m, index) => {
      const sku = explicitSkuOnly(null);
      const spec: OriginSpec = {
        product_description: m.description,
        category: m.category ? ({ id: m.category, label: m.category } as OriginRef) : null,
        base_substrate: null, width: m.width, height: m.height,
        single_double: 'single', graphic_method: null, vinyl_type: null,
        rta_color: null, laminate: null, taping: null,
        is_paneled: false, paneling_deferred_to_internal: false,
        panel_count: null, panel_width: null, panel_height: null, panel_notes: null,
        special_order_tier: null, hardware_needed: false, hardware_description: null,
        grommets: null, hem: null, pole_pocket: false, rounded_corners: null,
        mounting_holes: null, white_ink: null, reference_sku: null,
        design_notes: m.notes ?? null,
        ada_color_spec: null, ada_pictogram: null, ada_tactile_text: null, ada_braille: null, ada_build_notes: null,
        attachments: [],
        quantity: m.qty, notes: m.notes ?? null,
      };
      return baseSubject({
        sku,
        productType: 'custom',
        name: m.description,
        qty: m.qty,
        flags: buildTbdFlags(spec),
        spec,
      });
    });

  // Existing library SKUs added as ride-alongs (type: 'existing') — no PD work.
  const existingAddedChildren: Subject[] = p.added
    .filter((m): m is Extract<typeof m, { type: 'existing' }> => m.type === 'existing')
    .map(m => baseSubject({
      sku: m.sku, productType: 'standard', name: m.label ?? m.sku,
      deliverables: standardDeliverables(), qty: m.qty, flags: [], bundleDelta: 'add',
    }));

  // Original members staying in the bundle — needed for proof. Qty-changed ones
  // are tagged so they read distinctly from untouched ride-alongs.
  const qtyChangedSkus = new Set(p.modified.map(m => m.sku));
  const stayingMemberChildren: Subject[] = (p.existing_members ?? []).map(m => baseSubject({
    sku: m.sku, productType: 'standard', name: m.label ?? m.sku,
    deliverables: standardDeliverables(), qty: m.qty, flags: [],
    ...(qtyChangedSkus.has(m.sku) ? { bundleDelta: 'qty' as const } : {}),
  }));

  // Original members being removed — shown for proof documentation. The 'remove'
  // bundleDelta carries the visual signal; no SOP flag needed.
  const removedMemberChildren: Subject[] = p.removed.map(m => baseSubject({
    sku: m.sku, productType: 'standard', name: m.label ?? m.sku,
    deliverables: standardDeliverables(), qty: 1, flags: [], bundleDelta: 'remove',
  }));

  const children: Subject[] = [
    bundleChg,
    ...stayingMemberChildren,
    ...removedMemberChildren,
    ...newProductChildren,
    ...existingAddedChildren,
  ];

  const deltaDesc = [
    p.added.length   && `${p.added.length} added`,
    p.removed.length && `${p.removed.length} removed`,
    p.modified.length && `${p.modified.length} qty changed`,
  ].filter(Boolean).join(', ');

  const master: Master = {
    id: p.ref ?? genId(p.submitted_at, 'BUN'), isMaster: true, path: 'chg', vip: false,
    isBundle: true, bundleName: p.bundle_name,
    product: `${p.bundle_name} (edit)`,
    meta: `Bundle edit · ${deltaDesc}`,
    customer: p.submitted_by.team,
    submitter,
    submitDate, ...dueFromSubmitted(p.submitted_at),
    submittedAtIso: p.submitted_at,
    flags: [],
    children,
    activity: [
      originSubmitActivity(
        p.submitted_at,
        `${p.submitted_by.name} submitted bundle edit "${p.bundle_name}" — ${deltaDesc} via Origin`,
      ),
    ],
  };
  return master;
}

// ─── Dispatch ─────────────────────────────────────────────────────────────────

/**
 * Returns null when the payload produces no PDQ work — currently only an
 * all-standard New Order (no custom lines), which is handled entirely in HubSpot.
 */
export function originPayloadToRequest(payload: DdOriginPayload): Request | null {
  let req: Request | null = null
  switch (payload.tile) {
    case 'new_product':
      if (isOriginNewProductCartPayload(payload)) {
        if (payload.products.length === 1) {
          const first = payload.products[0];
          const { products: _products, ...base } = payload;
          req = fromNewProductSingle({
            ...base,
            tile: 'new_product',
            new_product_variant: 'single',
            spec: first.spec,
            sop_warnings: first.sop_warnings,
          });
          break
        }
        req = fromNewProductCart(payload);
        break
      }
      if (isOriginNewProductSinglePayload(payload)) {
        req = fromNewProductSingle(payload);
        break
      }
      req = null
      break
    case 'new_product_bundle': req = fromNewProductBundle(payload); break
    case 'modify_product':     req = fromModify(payload); break
    case 'clone_estimate':     req = fromClone(payload); break
    case 'new_order':          req = fromNewOrder(payload); break
    case 'discontinue_replace': req = fromDiscontinueReplace(payload); break
    case 'modify_bundle':       req = fromModifyBundle(payload); break
    default:
      req = null
  }
  return req ? mintFixtureLineKeys(req, 'provisional') : null
}
